import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { z } from "zod";
import type { MemoryDropReason, MemoryItemView, MemoryKind, MemorySensitivity, MemorySleepReport, RecallResult } from "../../shared/contracts";
import { IconChevronRight, IconLock, IconPlus, IconSearch, IconX } from "../components/icons";
import { SensitivityBadge, SensitivityPicker, WhyPanel } from "../components/memory-parts";
import { Badge, Button, cx, ErrorNotice, Field, Loading, Notice, PageTop, ROUND_BUTTON, ROUND_BUTTON_PRIMARY, Select, Textarea } from "../components/ui";
import { useSettings } from "../lib/agent";
import { api, errorText, isAbortError } from "../lib/api";
import { fmtDateTime, fmtDayWord, MEMORY_KIND_LABEL, MEMORY_SOURCE_LABEL } from "../lib/format";
import { useDebounced, useElementWidth, useNow, usePageTitle } from "../lib/hooks";
import { useResource } from "../lib/store";

/** Kinds the user can add or re-file by hand (the agent's own notes stay the agent's). */
const USER_KINDS: MemoryKind[] = ["fact", "preference", "relationship", "event", "instruction", "plan", "task"];

/** « Dit le 12 oct. », « Noté hier »…: how the memory came, and when. */
const SOURCE_WORDS: Partial<Record<MemoryItemView["source"], string>> = {
  extraction: "Dit",
  user: "Ajouté par toi",
  agent_tool: "Noté",
  import: "Importé",
};

function sourceLine(item: MemoryItemView, now: number): string {
  const when = fmtDayWord(item.recordedAt, now);
  const words = SOURCE_WORDS[item.source] ?? MEMORY_SOURCE_LABEL[item.source];
  return `${words} ${/^\d/.test(when) ? `le ${when}` : when}${item.learnedBy ? ` · ${item.learnedBy}` : ""}`;
}

const contentSchema = z.string().trim().min(1, "Écris quelque chose.").max(2000, "2 000 caractères maximum.");

const FILTERS: { kind: MemoryKind | "all"; label: string }[] = [
  { kind: "all", label: "Tout" },
  { kind: "relationship", label: "Personnes" },
  { kind: "preference", label: "Goûts" },
  { kind: "plan", label: "Projets" },
  { kind: "event", label: "Échéances" },
];

/** A memory forgotten a moment ago: erased for good when the « Annuler » window closes (or the page is left). */
interface Forgetting {
  item: MemoryItemView;
  timer: ReturnType<typeof setTimeout>;
}

const UNDO_MS = 5000;

/**
 * /memoire — what the agent knows (V2Memoire): search, filters, one row per memory (its words, where it comes from);
 * a memory opens beside the list (below it when narrow) with « Pourquoi ? » (the owner's own words), « Corriger » and
 * « Oublier » (undone within 5 s). « + » teaches it something; the night's sleep stays one discreet line.
 */
export function MemoryPage() {
  usePageTitle("Mémoire");
  const now = useNow(60_000);
  const settings = useSettings();
  const agentName = settings.data?.agentName ?? "ton agent";
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<MemoryKind | "all">("all");
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<MemoryItemView | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [forgetting, setForgetting] = useState<Forgetting | null>(null);
  const [changed, setChanged] = useState<Record<string, MemoryItemView>>({});
  const [error, setError] = useState<string | null>(null);
  const q = useDebounced(query.trim(), 350);
  const searchId = useId();
  // The detail beside the list, or under the selected row when narrow.
  const [layoutRef, layoutWidth] = useElementWidth<HTMLDivElement>();
  const side = layoutWidth >= 700;

  // Erase for good what was forgotten (when the undo window closes, or right away when the page is left).
  const erase = useCallback(async (item: MemoryItemView) => {
    try {
      await api("DELETE /api/memory/:id", { params: { id: item.id }, query: { hard: "1" } });
    } catch (err) {
      setError(errorText(err));
      setHidden((h) => {
        const next = new Set(h);
        next.delete(item.id);
        return next;
      });
    }
  }, []);
  const pendingRef = useRef<Forgetting | null>(null);
  pendingRef.current = forgetting;
  useEffect(
    () => () => {
      const f = pendingRef.current;
      if (f) {
        clearTimeout(f.timer);
        void erase(f.item);
      }
    },
    [erase],
  );

  function forget(item: MemoryItemView) {
    if (forgetting) {
      clearTimeout(forgetting.timer);
      void erase(forgetting.item);
    }
    setHidden((h) => new Set(h).add(item.id));
    setSelected(null);
    const timer = setTimeout(() => {
      setForgetting(null);
      void erase(item);
    }, UNDO_MS);
    setForgetting({ item, timer });
  }

  function undo() {
    if (!forgetting) return;
    clearTimeout(forgetting.timer);
    const { item } = forgetting;
    setForgetting(null);
    setHidden((h) => {
      const next = new Set(h);
      next.delete(item.id);
      return next;
    });
    setSelected(item);
  }

  const current = selected ? (changed[selected.id] ?? selected) : null;
  const detail = current ? (
    <MemoryDetail
      key={current.id}
      item={current}
      now={now}
      onChanged={(next) => {
        setChanged((c) => ({ ...c, [next.id]: next }));
        setSelected(next);
      }}
      onForget={() => forget(current)}
      onClose={() => setSelected(null)}
    />
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      <PageTop
        title={`Ce que ${agentName} sait de toi`}
        className="mb-0"
        actions={
          <button type="button" aria-label={`Apprendre quelque chose à ${agentName}`} aria-expanded={adding} onClick={() => setAdding((v) => !v)} className={adding ? ROUND_BUTTON : ROUND_BUTTON_PRIMARY}>
            {adding ? <IconX width={14} height={14} strokeWidth={2.6} /> : <IconPlus width={16} height={16} strokeWidth={2.6} />}
          </button>
        }
      />

      {adding ? <AddMemory agentName={agentName} onAdded={() => window.dispatchEvent(new Event("pulsez:memory-changed"))} /> : null}

      <SleepNotes now={now} />

      <div className="flex flex-wrap items-center gap-2.5">
        <div className="flex h-[42px] min-w-0 flex-[1_1_260px] items-center gap-2 rounded-full bg-elev px-3.5 shadow-[inset_0_0_0_1px_var(--border)] focus-within:shadow-[inset_0_0_0_1px_var(--border-strong)] max-md:h-11">
          <IconSearch width={16} height={16} strokeWidth={2} className="shrink-0 text-subtle" />
          <label htmlFor={searchId} className="sr-only">
            Chercher
          </label>
          <input
            id={searchId}
            type="search"
            value={query}
            maxLength={300}
            placeholder="Chercher"
            onChange={(e) => setQuery(e.target.value)}
            className="h-full min-w-0 flex-1 bg-transparent text-[15px] text-fg outline-none placeholder:text-muted"
          />
        </div>
        <div role="group" aria-label="Filtrer" className="flex flex-wrap gap-1">
          {FILTERS.map((f) => {
            const on = kind === f.kind;
            return (
              <button
                key={f.kind}
                type="button"
                aria-pressed={on}
                onClick={() => setKind(f.kind)}
                className={cx("h-[34px] rounded-full px-[13px] text-[13.5px] font-semibold transition max-md:h-11", on ? "bg-ink text-ink-fg" : "bg-bubble-agent text-fg hover:bg-chip")}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {error ? <ErrorNotice message={error} /> : null}

      <div ref={layoutRef} className="flex min-h-0 gap-5">
        <div className="min-w-0 flex-1">
          {q ? (
            <SearchResults q={q} now={now} hidden={hidden} changed={changed} selectedId={current?.id ?? null} onSelect={setSelected} inlineDetail={side ? null : detail} />
          ) : (
            <MemoryList kind={kind} now={now} hidden={hidden} changed={changed} selectedId={current?.id ?? null} onSelect={setSelected} inlineDetail={side ? null : detail} />
          )}
          {forgetting ? (
            <div role="status" className="cv-in mt-2.5 inline-flex items-center gap-3 rounded-[18px] bg-bubble-user py-2 pr-2 pl-3.5 text-[14px]">
              Oublié.
              <button type="button" onClick={undo} className="h-[30px] rounded-full bg-ink px-3 text-[13px] font-semibold text-ink-fg max-md:h-11">
                Annuler
              </button>
            </div>
          ) : null}
        </div>
        {side && detail ? <div className="w-[340px] shrink-0">{detail}</div> : null}
      </div>
    </div>
  );
}

/** Why the nightly sleep set a memory aside, in the user's words. */
const DROP_REASON_LABEL: Record<MemoryDropReason, string> = {
  trivial: "Sans intérêt pour la suite",
  transient: "État passager, déjà dépassé",
  redundant: "Déjà dit par un autre souvenir",
  outdated: "Remplacé par plus récent",
  meta: "Ce que ton agent a fait ou dit, pas un fait sur toi",
};

const plural = (n: number, word: string) => `${n} ${word}${n > 1 ? "s" : ""}`;

/**
 * The nightly sleep, discreetly: « Cette nuit : X souvenirs fusionnés, Y écartés », and the memories it set aside with
 * their reason and a « Restaurer » button (the memory comes back and is never sorted again).
 */
function SleepNotes({ now }: { now: number }) {
  const report = useResource<MemorySleepReport>("memory:sleep", async (signal) => api("GET /api/memory/sleep", { signal }), { staleMs: 30_000 });
  const [open, setOpen] = useState(false);
  const [restored, setRestored] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const data = report.data;
  if (!data || (!data.last && !data.dropped.length)) return null;
  const dropped = data.dropped.filter((d) => !restored.has(d.id));
  const last = data.last;
  const recent = last !== null && now - last.at < 30 * 3600_000;

  async function restore(id: string) {
    setBusy(id);
    setError(null);
    try {
      await api("POST /api/memory/:id/restore", { params: { id } });
      setRestored((s) => new Set(s).add(id));
      window.dispatchEvent(new Event("pulsez:memory-changed"));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="text-[12.5px] text-subtle">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {last ? (
          <span data-testid="sleep-line">
            {recent ? "Cette nuit" : `Nuit du ${fmtDateTime(last.at, now)}`} : {plural(last.merged, "souvenir")} fusionné{last.merged > 1 ? "s" : ""}, {last.dropped} écarté
            {last.dropped > 1 ? "s" : ""}
          </span>
        ) : null}
        {dropped.length ? (
          <button type="button" className="inline-flex min-h-8 items-center underline underline-offset-2 hover:text-fg max-md:min-h-11" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            Écartés ({dropped.length})
          </button>
        ) : null}
      </div>
      {open && dropped.length ? (
        <ul className="mt-3 space-y-2" aria-label="Souvenirs écartés">
          {dropped.map((d) => (
            <li key={d.id} data-testid="dropped-item" className="flex min-w-0 flex-wrap items-start gap-x-3 gap-y-1.5 rounded-[14px] bg-elev px-3.5 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] leading-relaxed text-fg wrap-anywhere">{d.content}</p>
                <p className="mt-0.5 text-[12px] text-subtle">
                  {DROP_REASON_LABEL[d.dropReason] ?? d.dropReason} · écarté {fmtDateTime(d.droppedAt, now)}
                </p>
              </div>
              <Button size="sm" variant="secondary" busy={busy === d.id} disabled={busy !== null} onClick={() => void restore(d.id)}>
                Restaurer
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <Notice tone="danger" role="alert" className="mt-2">
          {error}
        </Notice>
      ) : null}
    </div>
  );
}

/** The rows of a list: the memory's words, where it comes from; the detail under the selected row when narrow. */
function Rows({
  items,
  now,
  selectedId,
  onSelect,
  inlineDetail,
}: {
  items: MemoryItemView[];
  now: number;
  selectedId: string | null;
  onSelect: (item: MemoryItemView | null) => void;
  inlineDetail: ReactNode;
}) {
  return (
    <ul className="flex min-w-0 flex-col">
      {items.map((item) => {
        const on = item.id === selectedId;
        return (
          <li key={item.id} data-testid="memory-item">
            <button
              type="button"
              aria-pressed={on}
              onClick={() => onSelect(on ? null : item)}
              className={cx("flex w-full min-w-0 items-center gap-3 rounded-[14px] px-3.5 py-3 text-left transition", on ? "bg-bubble-agent" : "hover:bg-elev")}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] leading-snug wrap-anywhere">{item.content}</span>
                <span className="mt-[3px] flex flex-wrap items-center gap-x-1.5 text-[12.5px] text-subtle">
                  <span>{sourceLine(item, now)}</span>
                  {item.pinned ? <span>· épinglé</span> : null}
                  <SensitivityBadge sensitivity={item.sensitivity} />
                  {item.trust === "external" ? <Badge tone="neutral">Source extérieure</Badge> : null}
                </span>
              </span>
              {/* Turned down only when the detail opens under the row (narrow); beside the list it stays a plain chevron. */}
              <IconChevronRight width={14} height={14} strokeWidth={2.2} className={cx("shrink-0 text-subtle transition", on && Boolean(inlineDetail) && "rotate-90")} />
            </button>
            {on && inlineDetail ? <div className="mt-1 mb-2">{inlineDetail}</div> : null}
          </li>
        );
      })}
    </ul>
  );
}

function SearchResults({
  q,
  now,
  hidden,
  changed,
  selectedId,
  onSelect,
  inlineDetail,
}: {
  q: string;
  now: number;
  hidden: Set<string>;
  changed: Record<string, MemoryItemView>;
  selectedId: string | null;
  onSelect: (item: MemoryItemView | null) => void;
  inlineDetail: ReactNode;
}) {
  const [result, setResult] = useState<RecallResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api("GET /api/memory/search", { query: { q }, signal: controller.signal })
      .then((r) => setResult(r))
      .catch((err: unknown) => {
        if (!isAbortError(err)) setError(errorText(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [q]);

  if (loading && !result) return <Loading label="Recherche…" />;
  if (error) return <ErrorNotice message={error} />;
  const items = (result?.items ?? []).filter((i) => !hidden.has(i.id)).map((i) => changed[i.id] ?? i);
  if (!items.length) return <p className="px-1 py-4 text-[14.5px] text-muted">Rien trouvé.</p>;
  return <Rows items={items} now={now} selectedId={selectedId} onSelect={onSelect} inlineDetail={inlineDetail} />;
}

function MemoryList({
  kind,
  now,
  hidden,
  changed,
  selectedId,
  onSelect,
  inlineDetail,
}: {
  kind: MemoryKind | "all";
  now: number;
  hidden: Set<string>;
  changed: Record<string, MemoryItemView>;
  selectedId: string | null;
  onSelect: (item: MemoryItemView | null) => void;
  inlineDetail: ReactNode;
}) {
  const [items, setItems] = useState<MemoryItemView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (append: string | null) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      if (append) setMore(true);
      else setLoading(true);
      setError(null);
      try {
        const res = await api("GET /api/memory", {
          query: { ...(kind !== "all" ? { kind } : {}), status: "active", ...(append ? { cursor: append } : {}) },
          signal: controller.signal,
        });
        setItems((prev) => (append ? [...prev, ...res.items.filter((i) => !prev.some((p) => p.id === i.id))] : res.items));
        setCursor(res.cursor);
      } catch (err) {
        if (!isAbortError(err)) setError(errorText(err));
      } finally {
        if (controllerRef.current === controller) {
          setLoading(false);
          setMore(false);
        }
      }
    },
    [kind],
  );

  useEffect(() => {
    void load(null);
    const onChanged = () => void load(null);
    window.addEventListener("pulsez:memory-changed", onChanged);
    return () => {
      window.removeEventListener("pulsez:memory-changed", onChanged);
      controllerRef.current?.abort();
    };
  }, [load]);

  // Pinned first.
  const visible = items.filter((i) => !hidden.has(i.id)).map((i) => changed[i.id] ?? i);
  const ordered = [...visible.filter((i) => i.pinned), ...visible.filter((i) => !i.pinned)];

  return (
    <div className="flex flex-col gap-3">
      {error ? <ErrorNotice message={error} onRetry={() => void load(null)} /> : null}
      {loading ? (
        <Loading />
      ) : !ordered.length ? (
        <p className="px-1 py-6 text-[15px] text-muted">Rien encore.</p>
      ) : (
        <>
          <Rows items={ordered} now={now} selectedId={selectedId} onSelect={onSelect} inlineDetail={inlineDetail} />
          {cursor ? (
            <Button variant="secondary" busy={more} onClick={() => void load(cursor)} className="self-start">
              Voir plus
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}

/** One memory, opened: its words, « Pourquoi ? », « Corriger » (in place) and « Oublier ». */
function MemoryDetail({ item, now, onChanged, onForget, onClose }: { item: MemoryItemView; now: number; onChanged: (item: MemoryItemView) => void; onForget: () => void; onClose: () => void }) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(item.content);
  const [kind, setKind] = useState<MemoryKind>(item.kind);
  const [sensitivity, setSensitivity] = useState<MemorySensitivity>(item.sensitivity ?? "normal");
  const [busy, setBusy] = useState<"pin" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const contentId = useId();
  const kindId = useId();

  async function patch(body: { content?: string; pinned?: boolean; kind?: MemoryKind; sensitivity?: MemorySensitivity }, what: "pin" | "save") {
    setBusy(what);
    setError(null);
    try {
      const updated = await api("PATCH /api/memory/:id", { params: { id: item.id }, body });
      onChanged(updated);
      if (what === "save") setEditing(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const parsed = contentSchema.safeParse(content);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Contenu invalide.");
      return;
    }
    await patch({ content: parsed.data, ...(kind !== item.kind ? { kind } : {}), ...(sensitivity !== (item.sensitivity ?? "normal") ? { sensitivity } : {}) }, "save");
  }

  return (
    <aside aria-label="Souvenir" data-testid="memory-detail" className="cv-in flex flex-col gap-3.5 rounded-[22px] bg-elev p-[22px]">
      {editing ? (
        <form onSubmit={save} noValidate className="space-y-3">
          <Field label="Corriger" htmlFor={contentId}>
            <Textarea id={contentId} value={content} maxLength={2100} className="min-h-24 bg-bg" onChange={(e) => setContent(e.target.value)} />
          </Field>
          <Field label="Type" htmlFor={kindId}>
            <Select id={kindId} value={kind} onChange={(e) => setKind(e.target.value as MemoryKind)} className="bg-bg">
              {(USER_KINDS.includes(item.kind) ? USER_KINDS : [item.kind, ...USER_KINDS]).map((k) => (
                <option key={k} value={k}>
                  {MEMORY_KIND_LABEL[k]}
                </option>
              ))}
            </Select>
          </Field>
          <SensitivityPicker value={sensitivity} onChange={setSensitivity} />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" busy={busy === "save"}>
              Enregistrer
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(false);
                setContent(item.content);
                setKind(item.kind);
                setSensitivity(item.sensitivity ?? "normal");
                setError(null);
              }}
            >
              Annuler
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="flex items-start gap-2">
            <p className="min-w-0 flex-1 font-display text-[22px] leading-[1.25] wrap-anywhere">{item.content}</p>
            <button type="button" aria-label="Fermer" onClick={onClose} className={cx(ROUND_BUTTON, "-mt-1 -mr-1 bg-transparent text-subtle")}>
              <IconX width={13} height={13} strokeWidth={2.6} />
            </button>
          </div>
          <div>
            <p className="mb-1.5 text-[12.5px] font-semibold text-subtle">Pourquoi ?</p>
            <WhyPanel id={item.id} now={now} />
          </div>
          <p className="text-[12.5px] text-subtle">
            {MEMORY_KIND_LABEL[item.kind] ?? item.kind}
            {item.dueAt ? ` · échéance ${fmtDateTime(item.dueAt, now)}` : ""}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => setEditing(true)} className="md:h-9">
              Corriger
            </Button>
            <Button variant="outline" size="sm" disabled={busy !== null} onClick={onForget} className="md:h-9">
              Oublier
            </Button>
            <button type="button" disabled={busy !== null} onClick={() => void patch({ pinned: !item.pinned }, "pin")} className="ml-auto inline-flex min-h-9 items-center px-1 text-[13px] text-muted hover:text-fg max-md:min-h-11">
              {item.pinned ? "Détacher" : "Épingler"}
            </button>
          </div>
        </>
      )}
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
    </aside>
  );
}

/** « Apprends-lui quelque chose »: one field, « Retenir »; never a password (that is the vault's). */
function AddMemory({ agentName, onAdded }: { agentName: string; onAdded: () => void }) {
  const contentId = useId();
  const kindId = useId();
  const [content, setContent] = useState("");
  const [kind, setKind] = useState<MemoryKind>("fact");
  const [pinned, setPinned] = useState(false);
  const [sensitivity, setSensitivity] = useState<MemorySensitivity>("normal");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const parsed = contentSchema.safeParse(content);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Contenu invalide.");
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api("POST /api/memory", {
        body: { content: parsed.data, kind, ...(pinned ? { pinned: true } : {}), ...(sensitivity !== "normal" ? { sensitivity } : {}) },
      });
      setContent("");
      setPinned(false);
      setSensitivity("normal");
      setSaved(true);
      onAdded();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate aria-label={`Apprends-lui quelque chose`} className="cv-in flex flex-col gap-2.5 rounded-[22px] bg-elev p-5">
      <label htmlFor={contentId} className="sr-only">
        À retenir
      </label>
      <textarea
        id={contentId}
        value={content}
        rows={2}
        maxLength={2100}
        autoFocus
        placeholder={`Apprends quelque chose à ${agentName}`}
        aria-invalid={error ? true : undefined}
        onChange={(e) => {
          setContent(e.target.value);
          setSaved(false);
        }}
        className="resize-none rounded-[14px] bg-bg px-3.5 py-3 text-[15px] text-fg outline-none placeholder:text-muted focus:ring-2 focus:ring-line-strong"
      />
      {error ? (
        <p className="text-[13px] text-danger" role="alert">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" busy={busy} className="md:h-9">
          Retenir
        </Button>
        {saved ? (
          <span className="text-[13px] text-muted" role="status">
            C'est noté.
          </span>
        ) : null}
        <details className="group ml-auto">
          <summary className="inline-flex min-h-9 cursor-pointer list-none items-center gap-1 text-[13px] text-subtle select-none hover:text-fg max-md:min-h-11 [&::-webkit-details-marker]:hidden">
            Options
          </summary>
          <div className="mt-2 space-y-3">
            <Field label="Type de souvenir" htmlFor={kindId}>
              <Select id={kindId} value={kind} onChange={(e) => setKind(e.target.value as MemoryKind)} className="bg-bg">
                {USER_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {MEMORY_KIND_LABEL[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <label className="flex min-h-11 items-center gap-2 text-[13.5px]">
              <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} className="h-4 w-4" />
              Toujours en tête
            </label>
            <SensitivityPicker value={sensitivity} onChange={setSensitivity} />
          </div>
        </details>
      </div>
      <p className="flex items-center gap-2 text-[12.5px] text-subtle">
        <IconLock width={14} height={14} strokeWidth={2} className="shrink-0" />
        Jamais de mot de passe ici : utilise le Coffre.
      </p>
    </form>
  );
}
